import { createStore } from 'vuex';
import * as Sentry from '@sentry/vue';

import { objectsEqual } from '@/lib/compare';
import { EventLog } from '@/lib/event-log';
import { ANALYTICS_CAPABILITY, FORMULARY_CAPABILITY, INTELLIGENCE_CAPABILITY } from '@/lib/identity-methods';
import router, { getPendingPath } from '@/router/index';
import FiltersService from '@/services/filters';
import IdentityService from '@/services/identity';
import UsersService from '@/services/users';
import uploadFiles from './modules/upload-files';
import filters, { FORMULARY_MODULE, SAFETY_MODULE } from './modules/filters';
import menus from './modules/menus';
import notifications from './modules/notifications';
import reports from './modules/reports';
import requests from './modules/requests';
import tenant from './modules/tenant';
import user from './modules/user';
import users from './modules/users';
import reference from './modules/reference';
import medication from './modules/medication';
import discover from './modules/discover';
import investigation from './modules/investigation';
import analysis from './modules/analysis';
import opportunities from './modules/opportunities';

// Vue.use(Vuex);

// Shared by overlapping callers: tryWhoami and onIdTokenChanged both exchange on page load.
let exchangeInFlight = null;

// How long logout waits for the server to end the session before carrying on.
const LOGOUT_REQUEST_TIMEOUT_MS = 5000;

// export default new Vuex.Store({
const store = createStore({
  modules: {
    uploadFiles,
    filters,
    menus,
    notifications,
    reports,
    requests,
    tenant,
    user,
    users,
    reference,
    medication,
    discover,
    investigation,
    analysis,
    opportunities,
  },
  state: {
    app: null,
    loginComplete: true,
    tenantSwitchComplete: true,
    config: {},
  },
  mutations: {
    SET_APP(state, app) {
      state.app = app;
    },
    SET_CONFIG(state, config) {
      state.config = config;
    },
    SET_LOGIN_COMPLETE(state, status) {
      state.loginComplete = status;
    },
    SET_TENANT_SWITCH_COMPLETE(state, status) {
      state.tenantSwitchComplete = status;
    },
  },
  actions: {
    exchangeToken({ dispatch, state }, idToken) {
      if (exchangeInFlight) {
        return exchangeInFlight;
      }
      const exchange = new Promise((resolve, reject) => {
        const exchangeData = {
          grant_type: 'urn:bbh:params:oauth:grant-type:token-exchange',
          resource: ['https://www.app.bainbridgehealth.com'],
          subject_token: idToken,
          subject_token_type: 'urn:bbh:params:oauth:token-type:id_token',
        };
        store.dispatch('setAccessToken', idToken);
        IdentityService.exchangeToken(exchangeData)
          .then(async (res) => {
            if (res.error === 400) {
              dispatch('logout');
              window.location.replace('/');
              resolve();
              return;
            }
            const paths = window.location.href.split('/');
            if (paths[3] !== 't') {
              const params = new URLSearchParams(window.location.hash.split('?')[1]);
              const redirect = params.get('redirect') || ''; // "intelligence"
              window.location.replace(`/t/${res.default_tenant_id}/#${redirect}`);
              resolve();
            } else {
              const tenantId = paths[4];

              store.dispatch('setUserToken', res.access_token);
              const userInfo = res.user;
              const identityUser = state.app.config.globalProperties.$firebaseAuth.currentUser;
              let multiFactor;

              if (identityUser && identityUser.multiFactor && identityUser.multiFactor.enrolledFactors.length > 0) {
                [multiFactor] = identityUser.multiFactor.enrolledFactors;
                if (!userInfo.mfa_enabled) {
                  UsersService.postUserMfa(userInfo.id, { mfa_enabled: true });
                  userInfo.mfa_enabled = true;
                }
              } else if (userInfo.mfa_enabled) {
                UsersService.postUserMfa(userInfo.id, { mfa_enabled: false });
                userInfo.mfa_enabled = false;
              }

              dispatch('setUser', userInfo);
              dispatch('setMultiFactor', multiFactor);
              dispatch('setDefaultTenant', res.default_tenant_id);
              Promise.all([dispatch('getUserTenants'), dispatch('createTenantSession', tenantId)])
                .then(() => resolve())
                .catch((err) => reject(err));
            }
          })
          .catch((error) => {
            reject(error);
          })
          .finally(() => {
            dispatch('setLoginComplete', true);
          });
      });
      exchangeInFlight = exchange;
      const clearInFlight = () => {
        exchangeInFlight = null;
      };
      exchange.then(clearInFlight, clearInFlight);
      return exchange;
    },
    getUserTenants({ dispatch }) {
      return new Promise((resolve, reject) => {
        IdentityService.getUserTenants()
          .then((res) => {
            if (!res.tenants || res.tenants.length === 0) {
              throw new Error('No tenants associated with your user.');
            }
            dispatch('setTenants', res.tenants);
            resolve();
          })
          .catch(() => {
            reject();
          });
      });
    },
    createTenantSession({ dispatch, rootGetters }, tenantId) {
      return new Promise((resolve, reject) => {
        dispatch('setTenantSwitchComplete', false);
        IdentityService.getTenantSession(tenantId)
          .then((res) => {
            const options = {
              tenant: res.tenant,
              initialLoad: true,
            };

            if (rootGetters.tenantReauth) {
              resolve();
              return;
            }
            const query = { ...router.currentRoute.value.query };
            const tenantQuery = /[&?]tenant=\w*&*/;

            if (query.redirect && tenantQuery.test(query.redirect)) {
              query.redirect = query.redirect.replace(tenantQuery, '');
              if (!objectsEqual(router.currentRoute.value.query, query)) {
                router.push({ query });
              }
            }

            dispatch('setTenantToken', res.tenant_token);
            dispatch('setReauthenticateRequired', false);
            dispatch('setCapabilities', res.capabilities);
            dispatch('updateTenant', options);
            dispatch('setInactivitySessionTimer');
            resolve();
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    login({ state, dispatch, rootGetters }, fromClaims) {
      return new Promise((resolve, reject) => {
        let request = IdentityService.postLogin;
        if (fromClaims) {
          request = rootGetters.claimsToken ? IdentityService.getUserClaims : IdentityService.getClaimsToken;
        }
        request()
          .then((json) => {
            if (json.claims_token) {
              dispatch('setClaimsToken', { claimsToken: json.claims_token });
            }

            if (!json.tenants || json.tenants.length === 0) {
              throw new Error('No tenants associated with your user.');
            }
            const query = { ...router.currentRoute.value.query };
            const userInfo = json.user;
            const { tenants, capabilities } = json;
            let currentTenant = tenants.find((t) => t.id === json.tenant_id);
            const tenantQuery = /[&?]tenant=\w*&*/;
            if (query.redirect && tenantQuery.test(query.redirect)) {
              const tenantQueryParam = query.redirect.split('tenant=')[1].split('&')[0];
              const queryTenant = tenants.find((t) => t.name === tenantQueryParam);
              currentTenant = queryTenant || currentTenant;
              query.redirect = query.redirect.replace(tenantQuery, '');
              if (!objectsEqual(router.currentRoute.value.query, query)) {
                router.push({ query });
              }
            }

            const defaultTenant = tenants.find((t) => t.is_default) || tenants[0];
            const options = {
              tenant: currentTenant,
              initialLoad: true,
            };
            const identityUser = state.app.config.globalProperties.$firebaseAuth.currentUser;
            let multiFactor;
            if (identityUser && identityUser.multiFactor && identityUser.multiFactor.enrolledFactors.length > 0) {
              [multiFactor] = identityUser.multiFactor.enrolledFactors;
              if (!userInfo.mfa_enabled) {
                UsersService.postUserMfa(userInfo.id, { mfa_enabled: true });
                userInfo.mfa_enabled = true;
              }
            } else if (userInfo.mfa_enabled) {
              UsersService.postUserMfa(userInfo.id, { mfa_enabled: false });
              userInfo.mfa_enabled = false;
            }

            dispatch('setUser', userInfo);
            dispatch('setMultiFactor', multiFactor);
            dispatch('setDefaultTenant', defaultTenant.id);
            dispatch('setTenants', tenants);
            dispatch('setReauthenticateRequired', false);
            dispatch('setCapabilities', capabilities);
            dispatch('updateTenant', options);
            if ('track' in query) {
              const body = {
                token: query.track,
              };
              UsersService.postTrackTokenRead(userInfo.id, body)
                .then((result) => {
                  if (result.error) {
                    const errorData = new EventLog({
                      event: 'debug.fail_set_track_id',
                      error: result.error,
                    });
                    UsersService.postTrackingLog(errorData);
                  } else {
                    const trackId = result.track_id;
                    dispatch('setDebugUser', true);
                    sessionStorage.setItem('track_id', trackId);
                    Sentry.setTag('track_id', trackId);
                    const loggingData = new EventLog({
                      event: 'debug.set_track_id',
                      trackId,
                    });
                    UsersService.postTrackingLog(loggingData);
                  }
                })
                .catch((error) => {
                  const errorData = new EventLog({
                    event: 'debug.fail_set_track_id',
                    error: error.message,
                  });
                  UsersService.postTrackingLog(errorData);
                })
                .finally(() => {
                  if (!objectsEqual(router.currentRoute.value.query, query)) {
                    router.push({ query });
                  }
                });
            }
            if (!fromClaims) {
              const successLog = new EventLog({
                event: 'account.log_in',
                email: userInfo.email,
              });
              UsersService.postTrackingLog(successLog);
            }
            dispatch('refreshAccessToken');
            // dispatch('setInactivitySessionTimer');
            dispatch('setSessionTimer');
            resolve();
          })
          .catch((error) => {
            reject(error);
          })
          .finally(() => {
            dispatch('setLoginComplete', true);
          });
      });
    },
    logout({ state, dispatch, rootGetters }) {
      // End the server-side session on every logout path, without letting a slow server block it.
      const endServerSession = rootGetters.user
        ? Promise.race([
            IdentityService.postLogout(rootGetters.configAuth).catch(() => {}),
            new Promise((resolve) => {
              setTimeout(resolve, LOGOUT_REQUEST_TIMEOUT_MS);
            }),
          ])
        : Promise.resolve();
      return endServerSession.then(
        () =>
          new Promise((resolve, reject) => {
            const successLog = new EventLog({
              event: 'account.log_out',
              email: rootGetters.user ? rootGetters.user.email : null,
            });
            UsersService.postTrackingLog(successLog);
            dispatch('resetRequests');
            dispatch('resetUser');
            dispatch('resetTenant');
            dispatch('resetFilters');
            dispatch('resetReports');
            dispatch('resetUsers');
            dispatch('resetMenus');
            dispatch('setLoginComplete', true);
            dispatch('setTenantSwitchComplete', true);
            if (SENTRY_ENABLED) {
              Sentry.setUser(null);
            }
            state.app.config.globalProperties.$firebaseAuth
              .signOut()
              .then(() => {
                resolve();
              })
              .catch(() => {
                reject();
              });
          })
      );
    },
    setLoginComplete({ commit }, status) {
      commit('SET_LOGIN_COMPLETE', status);
    },
    setTenantSwitchComplete({ commit }, status) {
      commit('SET_TENANT_SWITCH_COMPLETE', status);
    },
    setConfig({ commit }, config) {
      commit('SET_CONFIG', config);
    },
    setTenantData({ dispatch, rootGetters }, options) {
      return new Promise((resolve, reject) => {
        const initialLoad = options ? options.initialLoad : false;
        const tenantInfo = options && options.tenant ? options.tenant : rootGetters.tenant;
        const startPath = router.currentRoute.value.path;
        const userCapabilities = rootGetters.capabilities;
        dispatch('resetUsers');
        dispatch('setTenant', tenantInfo);
        // Each module loads its own slice of the shared filter catalog, gated on its own
        // capability. A user with both reaches both; a user with neither loads no filters at all.
        //
        // `/api/filters/` returns that whole catalog, so it is fetched ONCE here and handed to
        // every module. Letting each module fetch its own meant two identical GETs racing on load.
        const filterModules = [
          [ANALYTICS_CAPABILITY, SAFETY_MODULE],
          [FORMULARY_CAPABILITY, FORMULARY_MODULE],
        ].filter(([capability]) => userCapabilities.includes(capability));
        if (filterModules.length) {
          filterModules.forEach(([, module]) => {
            dispatch('setFiltersLoaded', { module, filtersLoaded: false });
            dispatch('setUserFilterSetsLoaded', { module, userFilterSetsLoaded: false });
          });
          // Only the initial-load path reads the catalog; the other branch re-derives from state.
          const catalog = initialLoad ? FiltersService.getFilters(true) : null;
          // Same for the user's filter sets: one request shared by every module.
          const sharedSets = initialLoad ? FiltersService.getUserFilterSets(rootGetters.user.id, true) : null;
          sharedSets?.catch(() => {}); // each module handles failure itself
          filterModules.forEach(([, module]) => {
            dispatch('setFilters', { module, initialLoad, catalog }).then(() => {
              dispatch('setUserFilterSets', { module, initialLoad, sharedSets });
            });
          });
        }
        if (userCapabilities.includes(INTELLIGENCE_CAPABILITY)) {
          dispatch('setReportsState', 'loading');
          dispatch('setReports', tenantInfo);
        }
        dispatch('setMenus').then(() => {
          const allMenus = rootGetters.menus.concat(rootGetters.navbarItems);
          // setMenus is a network call, so the user may have navigated while it was in
          // flight. Two cases, and both have to be caught: the navigation committed
          // (currentRoute moved), or it is still pending — module routes are lazy
          // loaded, so a first-time module click leaves the target route uncommitted
          // for as long as its chunk takes to fetch, and currentRoute still reads as
          // the landing page throughout. Pushing in either case cancels a navigation
          // the user deliberately started and drops them back where they were.
          const pendingPath = getPendingPath();
          const routerPath = pendingPath ?? router.currentRoute.value.path;
          const userMovedSince = routerPath !== startPath;
          const paths = routerPath.split('/');
          const currentPath = paths[1];
          if (
            !userMovedSince &&
            currentPath &&
            tenantInfo.allowed_menus &&
            !tenantInfo.allowed_menus.includes(currentPath)
          ) {
            const allowedMenu = allMenus.find((menu) => menu.url === tenantInfo.allowed_menus[0]) || allMenus[0];
            const path = `/${allowedMenu.url}`;
            if (routerPath !== path) {
              router.push({ path });
            }
          }
          if (!initialLoad && !userMovedSince) {
            const defaultMenu = allMenus[0];
            const defaultSub = defaultMenu.subs && defaultMenu.subs.length > 0 ? defaultMenu.subs[0] : null;
            const defaultSubSection = defaultSub && 'sections' in defaultSub ? defaultSub.sections[0] : null;
            const currentMenu = allMenus.find((menu) => menu.url === currentPath);
            let defaultPath;
            if (defaultSubSection) {
              defaultPath = `/${defaultMenu.url}/${defaultSubSection.url}`;
            } else if (defaultSub) {
              defaultPath = `/${defaultMenu.url}/${defaultSub.url}`;
            } else {
              defaultPath = `/${defaultMenu.url}`;
            }
            if (!currentPath) {
              router.push({ path: defaultPath });
            } else if (paths.length > 2) {
              const subPath = paths[2];
              if (!currentMenu) {
                router.push({ path: defaultPath });
              } else if (currentMenu.subs) {
                const urls = [];
                currentMenu.subs.forEach((sub) => {
                  if ('sections' in sub) {
                    sub.sections.forEach((section) => urls.push(section.url));
                  } else if ('url' in sub) {
                    urls.push(sub.url);
                  }
                });
                if (!urls.includes(subPath)) {
                  const path = `/${currentMenu.url}/${urls[0]}`;
                  router.push({ path });
                }
              }
            }
          }
          dispatch('setCurrentPath');
          resolve();
        });
      });
    },
    updateTenant({ dispatch, rootGetters }, options) {
      return new Promise((resolve, reject) => {
        dispatch('setTenantSwitchComplete', false);
        const withClaims = options ? options.withClaims : false;
        const resetRequests = options ? options.resetRequests : false;
        const tenantInfo = options && options.tenant ? options.tenant : rootGetters.tenant;
        if (resetRequests) {
          dispatch('resetRequests');
        }
        if (withClaims) {
          dispatch('setClaimsToken', { tenantId: tenantInfo.id }).then(() => {
            dispatch('setTenantData', options).then(() => {
              dispatch('setTenantSwitchComplete', true);
              resolve();
            });
          });
        } else {
          dispatch('setTenantData', options).then(() => {
            dispatch('setTenantSwitchComplete', true);
            resolve();
          });
        }
      });
    },
  },
  getters: {
    app: (state) => state.app,
    loginComplete: (state) => state.loginComplete,
    tenantSwitchComplete: (state) => state.tenantSwitchComplete,
    config: (state) => state.config,
    configAuth: (state) => state.config.authVersion,
  },
  strict: VUE_STORE_STRICT,
});

export default store;
